import { Component, OnInit, OnDestroy, Renderer2, ElementRef } from '@angular/core';
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../../common/utils/froala.utils';
import { Router, ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, FormControl, Validators } from "@angular/forms";
import { Subscription, Observable } from "rxjs";
import { tap } from "rxjs/operators";

import { deepClone } from "../../../builder/models/editor.model";
import { genId } from "../../../core/services/genId";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { LoaderService } from "../../../common/services/loader.service";
import { AuthService } from "../../../services/auth.service";
import { HelpLibraryService } from "../../services/help-library.service";
import { HelpItem, HelpCategory } from "../../models/help-library.model";

@Component({
    selector: 'app-help-library-editor',
    templateUrl: './help-library-editor.component.html',
    styleUrls: ['./help-library-editor.component.scss'],
    standalone: false
})
export class HelpLibraryEditorComponent implements OnInit, OnDestroy {

    get addEditTitle(): string {
        return (this.isNew) ? "Add New Help Topic" : "Edit Help Topic";
    }
    
    addNewCategory: HelpCategory = new HelpCategory();
    categoryConfirmMessage: string = "";

    private _editorOptions: any = null;
    get editorOptions(): Object {
        if (!this._editorOptions) {
            let conf = window["PdfConfig"] as any;
            let key = (conf != null) ? conf.editorLicenseKey : "";
            this._editorOptions = applyFroalaAriaFix({
                heightMin: 300,
                placeholderText: 'Enter Help Topic Content',
                key: key
            });
        }
        return this._editorOptions;
    };

    categoryForm: FormGroup;
    helpCategory: HelpCategory = new HelpCategory();
    helpForm: FormGroup;
    helpItem: HelpItem;
    helpCategories: HelpCategory[] = [];
    helpCategoryEditorList: HelpCategory[] = [];

    isNew: boolean = false;

    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", true, () => this.onDialogClose())]);
    helpSavedDialog: Dialog = new Dialog("Help Topic has been saved.", [new DialogButton("OK", true, () => this.onHelpSaved())]);
    missingCategoryDialog: Dialog = new Dialog("You must select an existing category to create a new help topic.", [new DialogButton("OK", true, () => this.onDialogClose())]);
    notImplementedDialog: Dialog = new Dialog("This function has not yet been implemented.", [new DialogButton("Sorry!", true, () => this.onDialogClose())]);

    originalHelpItem: HelpItem;

    get editingName(): string {
        return this.helpItem.name;
    }

    subscription: Subscription = new Subscription();

    constructor(
        public router: Router,
        public activatedRoute: ActivatedRoute,
        private builder: FormBuilder,
        private _renderer: Renderer2,
        private _elementRef: ElementRef,
        public authService: AuthService,
        public dialogService: DynamicDialogService,
        public loader: LoaderService,
        public helpLibraryService: HelpLibraryService
    ) { }

    ngOnInit() {
        let helpCategory$ = this.helpLibraryService.getCategories().pipe(tap((categories) => {
            this.helpCategories = deepClone(categories);
            this.helpCategoryEditorList = deepClone(categories);
            this.addNewCategory.id = "new";
            this.addNewCategory.name = "Add New";
            this.helpCategoryEditorList.unshift(this.addNewCategory);
            this.buildCategoryForm();
        })).subscribe(
            () => {
                console.log("categories loaded");
            },
            (error) => {
                console.error(error);
                this.showError();
            });
        this.subscription.add(helpCategory$);
        let categoryCreated$ = this.helpLibraryService.onCategoryCreated.subscribe((category) => {
            this.helpCategories.push(category);
            this.sortCategories();
            this.resetCategoryEditorList();
            this.categoryConfirmMessage = `Category ${category.name} has been added.`;
            setTimeout(() => { this.categoryConfirmMessage = ""; }, 2500);
            this.cancelCategory();
        }, (error) => {
                console.error(error);
                
        });
        this.subscription.add(categoryCreated$);
        let categoryUpdated$ = this.helpLibraryService.onCategoryUpdated.subscribe((category) => {
            let index = this.helpCategories.findIndex(c => c.id === category.id);
            if (index !== -1) {
                this.helpCategories[index] = category;
            }
            this.sortCategories();
            this.resetCategoryEditorList();
            this.categoryConfirmMessage = `Category ${category.name} has been updated.`;
            setTimeout(() => { this.categoryConfirmMessage = ""; }, 2500);
            this.cancelCategory();
        }, (error) => {
            console.error(error);
            this.errorDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.errorDialog);
        });
        this.subscription.add(categoryUpdated$);
        
        let routeSubs$ = this.activatedRoute.data.subscribe((data) => {
            if (data.help) {
                this.originalHelpItem = data.help;
                this.helpItem = deepClone(data.help);                
            } else {
                this.isNew = true;
                this.helpItem = new HelpItem();
                this.helpItem.name = "New Help Topic";
                this.helpItem.lastUpdatedBy = "";
                this.helpItem.lastUpdatedById = this.authService.User.id;
            }
            this.helpLibraryService.setActiveNavItem(this.helpItem);
            this.buildHelpForm();   
            this.buildCategoryForm();
        }, 
            (error) => {
                console.error(error);
                this.errorDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.errorDialog);        
                this.helpForm = new FormGroup({});
        });
        this.subscription.add(routeSubs$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    buildCategoryForm(): void {
        this.categoryForm = this.builder.group({
            categorySelect: new FormControl(this.helpCategory.id, [Validators.required]),
            categoryName: new FormControl(this.helpCategory.name, [Validators.required]),
        });
        this.categoryForm.controls['categorySelect'].patchValue(this.helpCategory.id);
    }

    buildHelpForm(): void {
        let uri = (this.helpItem.uri != null) ? this.helpItem.uri
            : (this.isNew)
                ? ""
                :  `/help-library/view/${this.helpItem.categoryId}/${this.helpItem.id}`;
        this.helpForm = new FormGroup({
            name: new FormControl(this.helpItem.name, [Validators.required]),
            content: new FormControl(this.helpItem.data, [Validators.required]),
            category: new FormControl(this.helpItem.categoryId, [Validators.required]),
            relativeUrl: new FormControl({ value: uri, disabled: true }),
            directUrl: (this.isNew) ? new FormControl({ value: "", disabled: true })
                : new FormControl({ value: `${window.location.origin}/help-library/view/${this.helpItem.categoryId}/${this.helpItem.id}`, disabled: true }),
            updatedBy: new FormControl({ value: this.helpItem.lastUpdatedBy, disabled: true }),
            updatedDate: new FormControl({ value: this.helpItem.lastUpdatedDate, disabled: true})
        });

    }

    cancel(): void {
        if (this.isNew) {
            this.helpLibraryService.setActiveNavItem(null);
            this.router.navigateByUrl("help-library");
        } else {
            this.router.navigateByUrl(`help-library/${this.helpItem.categoryId}/${this.helpItem.id}`);
        }
    }

    cancelCategory(): void {
        this.helpCategory = new HelpCategory();
        this.buildCategoryForm();
    }

    

    copyValueToClipboard(field: string): void {
        let val = this.helpForm.controls[field].value;        
        let inputEl = this._renderer.createElement('input');
        inputEl.value = val;
        this._renderer.appendChild(this._elementRef.nativeElement, inputEl);
        inputEl.select();
        document.execCommand('copy');
        this._renderer.removeChild(this._elementRef.nativeElement, inputEl);
    }

    onCategoryChanged(): void {
        const selectedId = this.categoryForm.controls["categorySelect"].value;
        if (selectedId === this.addNewCategory.id) {
            this.helpCategory = new HelpCategory();
            this.helpCategory.id = this.addNewCategory.id;
        } else {
            let cat = this.helpCategoryEditorList.find(hc => hc.id === selectedId);
            if (cat != null) {
                this.helpCategory = deepClone(cat);
            }
        }
        this.buildCategoryForm();
    }

    onDialogClose(): void { };

    onHelpSaved(): void {
        this.router.navigateByUrl(`help-library/${this.helpItem.categoryId}/${this.helpItem.id}`);
    }

    resetCategoryEditorList(): void {
        this.helpCategoryEditorList = deepClone(this.helpCategories);
        this.helpCategoryEditorList.unshift(this.addNewCategory);
    }

    save(): void {
        this.validateHelpForm();
        if (this.helpForm.valid) {
            if (this.helpItem.categoryId !== this.addNewCategory.id) {
                let saveObs$ = (this.isNew)
                    ? this.helpLibraryService.createHelpItem(this.helpItem)
                    : this.helpLibraryService.editHelpItem(this.helpItem);
                this.loader.toggleLoader(true);
                let saveSubs$ = saveObs$.pipe().subscribe(
                    (saved) => {
                        this.helpItem = saved;
                        this.buildHelpForm();
                        this.helpLibraryService.setActiveNavItem(saved);
                        this.loader.toggleLoader(false);
                        this.helpSavedDialog.toggleDialog(true);
                        this.dialogService.updateDialog(this.helpSavedDialog);
                    },
                    (error) => {
                        console.error(error);
                        this.loader.toggleLoader(false);
                        this.showError();
                    }
                );
                this.subscription.add(saveSubs$);
            } else {
                this.missingCategoryDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.missingCategoryDialog);
            }
        }        
    }

    saveCategory(): void {
        this.validateCategoryForm();        
        if (this.categoryForm.valid) {
            this.loader.toggleLoader(true);

            let catObs$ = (this.helpCategory.id === this.addNewCategory.id)
                ? this.helpLibraryService.createCategory(this.helpCategory)
                : this.helpLibraryService.editCategory(this.helpCategory);

            let catSubs$ = catObs$.pipe().subscribe(
                (updated) => {
                    this.sortCategories();
                    this.resetCategoryEditorList();
                    this.helpCategory = new HelpCategory();
                    this.buildCategoryForm();
                    this.loader.toggleLoader(false);                    
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                    this.errorDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.errorDialog);
                }
            );
            this.subscription.add(catSubs$);
        }        
    }

    showError(): void {
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    sortCategories(): void {
        this.helpCategories.sort((a, b) => {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
        });
    }

    validateCategoryForm(): void {
        this.categoryForm.controls['categorySelect'].updateValueAndValidity();
        this.categoryForm.controls['categoryName'].updateValueAndValidity();
        this.helpCategory.name = this.categoryForm.controls['categoryName'].value;        
    }

    validateHelpForm(): void {
        this.helpForm.controls["name"].updateValueAndValidity();
        this.helpForm.controls["category"].updateValueAndValidity();
        this.helpForm.controls["content"].updateValueAndValidity();
        this.helpItem.name = this.helpForm.controls["name"].value;
        this.helpItem.categoryId = this.helpForm.controls["category"].value;
        this.helpItem.data = stripFroalaWatermark(this.helpForm.controls["content"].value);      
    }
};
